import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {ColumnProps} from 'antd/lib/table';
import {Button, Modal, Table, Tooltip} from 'antd';
import {Link} from 'react-router-dom';

import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {
  ErrorResponse, InventorySaleNumber,
  InventoryStock,
  InventoryStockItemStateFilter,
  PageResponseEnvelope,
  User,
  Warehouse,
} from '../api/dtos/dtos';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import {formatDateTimeOrNull} from '../utils/date-utils';
import * as paths from '../paths';
import ButtonBar from '../common/ButtonBar';
import accountProvider from '../account/AccountProvider';
import InventoryStockForm from "../inventory-stock-details/InventoryStockForm";
import {showSaveSuccessNotification} from "../utils/dialogs";
import ButtonGroup from "antd/lib/button/button-group";
import {groupNumber} from "../utils/number-utils";
import InventoryStockItemStateSearch from "../inventory-stock-details/InventoryStockItemStateSearch";

interface InventoryStocksLocationState extends Partial<GridState<InventoryStock>> {
  stateFilter?: InventoryStockItemStateFilter;
}

interface InventoryStocksPageState extends GridState<InventoryStock> {
  error?: ErrorResponse;
  saveError?: ErrorResponse;
  editedInventoryStock?: InventoryStock;
  modalTitle?: string;
  onModalSave?: (request: InventoryStock) => void;
  brands: string[];
  types: string[];
  names: string[];
  users: User[];
  warehouses: Warehouse[];
  saleNumbers: InventorySaleNumber[];
  stateFilter?: InventoryStockItemStateFilter;
}

class InventoryStocksPage extends React.Component<RouteComponentProps<{}, {}, InventoryStocksLocationState>, InventoryStocksPageState> {

  public state: InventoryStocksPageState = {
    ...tableUtils.initialGridState(),
    sortColumnKey: 'inventoryNumber',
    sortOrder: 'ascend',
    ...this.props.location.state,
    brands: [],
    types: [],
    names: [],
    users: [],
    warehouses: [],
    saleNumbers: [],
    stateFilter: this.props.location.state?.stateFilter,
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
    this.loadBrands();
    this.loadTypes();
    this.loadNames();
    this.loadUsers();
    this.loadWarehouses();
    this.loadSaleNumbers();
  }

  public render() {
    const {
      error,
      saveError,
      editedInventoryStock,
      modalTitle,
      onModalSave,
      brands,
      types,
      names,
      users,
      warehouses,
      saleNumbers,
      stateFilter
    } = this.state;

    const canCreate = accountProvider.isAdmin();
    const columns: Array<ColumnProps<InventoryStock>> = [
      searchableSortableColumn(locales.item_number, 'inventoryNumber', this.state, this.renderInventoryNumberColumn),
      searchableSortableColumn(locales.name, 'name', this.state, r => r.name),
      searchableSortableColumn(locales.brand, 'brand', this.state, r => r.brand),
      searchableSortableColumn(locales.type, 'type', this.state, r => r.type),
      sortableColumn(locales.created_at, 'createdAt', this.state, this.renderCreatedAt),
      sortableColumn(locales.available_count, 'availableCount', this.state, this.renderAvailableCountColumn),
      {key: 'actions', width: 48, render: this.renderActionColumn},
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <InventoryStockItemStateSearch
          users={users}
          saleNumbers={saleNumbers}
          warehouses={warehouses}
          stateFilter={stateFilter}
          onOk={this.onStateFilterApply}
          onClear={this.onStateFilterClear}
        />

        {canCreate && (
          <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus" onClick={this.onNewInventoryStock}>
              {locales.add}
            </Button>
          </ButtonBar>
        )}

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.indexRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 960}}
        />

        <Modal
          title={modalTitle}
          visible={editedInventoryStock !== undefined}
          footer={null}
          destroyOnClose={true}
          onCancel={this.onModalCancel}
        >
          {editedInventoryStock && <InventoryStockForm
              stock={editedInventoryStock.id ? editedInventoryStock : undefined}
              brands={brands}
              types={types}
              names={names}
              onFormSubmit={onModalSave}
              onFormCancel={this.onModalCancel}
          />}
          {saveError && <ErrorAlert style={{marginTop: '16px'}} error={saveError}/>}
        </Modal>

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderInventoryNumberColumn = (stock: InventoryStock): React.ReactNode => {
    return stock.id ?
      <Link to={{
        pathname: paths.viewInventoryStockPath(stock.id),
        state: {
          stateFilter: this.state.stateFilter,
        },
      }}>
        {stock.inventoryNumber}
      </Link> : stock.inventoryNumber;
  };

  private renderAvailableCountColumn = (stock: InventoryStock): React.ReactNode => {
    if (!stock.infiniteStock) {
      return `${groupNumber(stock.availableCount)} ${locales.pcs}`
    }

    return (
      <Tooltip title={locales.infinite_stock}>
        ∞
      </Tooltip>
    );
  };

  private renderCreatedAt = (stock: InventoryStock): string => {
    return formatDateTimeOrNull(stock.createdAt) || '-';
  };

  private renderActionColumn = (_: any, record: InventoryStock, __: number) => {
    const onEditInventoryStockClick = () => this.onEditInventoryStockClick(record);
    return <div className="action-column" style={{textAlign: 'right'}}>
      <ButtonGroup>
        <Button icon="edit"
                onClick={onEditInventoryStockClick}
                title={locales.edit}/>
      </ButtonGroup>
    </div>;
  };


  private onNewInventoryStock = () => {
    this.setState({
      editedInventoryStock: {
        name: '',
        inventoryNumber: '',
        brand: '',
        type: '',
        infiniteStock: false,
        id: undefined,
        createdAt: undefined,
      },
      onModalSave: this.onInventoryStockCreate,
      modalTitle: locales.inventory_stock_create,
      saveError: undefined,
    });
  };

  private onEditInventoryStockClick = (stock: InventoryStock) => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    this.setState({
      editedInventoryStock: stock,
      onModalSave: this.onInventoryStockSave,
      modalTitle: locales.inventory_stock_edit,
      saveError: undefined,
    });
  };


  private onGridChange = (newState: GridState<InventoryStock>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const {stateFilter} = this.state;
    const request = {
      ...tableUtils.toGridRequest(this.state),
      stateFilter: stateFilter ? {
        targetType: stateFilter.targetType,
        userId: stateFilter.userId,
        warehouseId: stateFilter.warehouseId,
        itemId: stateFilter.item?.id,
        saleId: stateFilter.saleId,
      } : undefined,
    };
    const promise = furoApi.inventoryStocks.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onStateFilterApply = (stateFilter: InventoryStockItemStateFilter) => {
    this.props.history.replace({
      ...this.props.location,
      state: {...this.props.location.state, stateFilter},
    });
    this.setState({page: 1, stateFilter}, () => this.reload());
  };

  private onStateFilterClear = () => {
    this.props.history.replace({
      ...this.props.location,
      state: {...this.props.location.state, stateFilter: undefined},
    });
    this.setState({page: 1, stateFilter: undefined}, () => this.reload());
  };

  private onReloadSuccess = (response: PageResponseEnvelope<InventoryStock>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };


  private onInventoryStockCreate = (request: InventoryStock) => {
    const promise = furoApi.inventoryStocks.create(request)
      .then(this.onInventoryStockSaved)
      .catch(this.onSaveApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onInventoryStockSave = (request: InventoryStock) => {
    const promise = furoApi.inventoryStocks.save(request, request.id!)
      .then(this.onInventoryStockSaved)
      .catch(this.onSaveApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onInventoryStockSaved = () => {
    this.setState({
      editedInventoryStock: undefined,
      error: undefined,
    });
    showSaveSuccessNotification();
    this.reload();
  };

  private onModalCancel = () => {
    this.setState({editedInventoryStock: undefined, saveError: undefined});
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onSaveApiError = (error: ErrorResponse) => {
    this.setState({saveError: error});
  };

  private loadBrands = () => {
    const promise = furoApi.inventoryStocks.getBrands()
      .then(this.onLoadBrandsSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadBrandsSuccess = (brands: string[]) => {
    this.setState({brands});
  };

  private loadTypes = () => {
    const promise = furoApi.inventoryStocks.getTypes()
      .then(this.onLoadTypesSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadTypesSuccess = (types: string[]) => {
    this.setState({types});
  };

  private loadNames = () => {
    const promise = furoApi.inventoryStocks.getNames()
      .then(this.onLoadNamesSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };
  private onLoadNamesSuccess = (names: string[]) => {
    this.setState({names});
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then((users) => this.setState({
        users: users.filter(user => user.enabled && user.userName),
      }))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadWarehouses = () => {
    const promise = furoApi.warehouses.getWarehouses()
      .then((warehouses) => this.setState({warehouses}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadSaleNumbers = () => {
    const promise = furoApi.sales.getAllSaleNumbers()
      .then((saleNumbers) => this.setState({saleNumbers}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };
}

export default withRouter(InventoryStocksPage);




// WEBPACK FOOTER //
// ./src/inventory-stock/InventoryStocksPage.tsx